import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@/components/icon/Icon";
import { api, ApiError } from "@/lib/api";
import { InquiryPanel } from "@/pages/admin/InquiryPanel";
import { PortfolioPanel } from "@/pages/admin/PortfolioPanel";
import { PricingPanel } from "@/pages/admin/PricingPanel";
import { VisitPanel } from "@/pages/admin/VisitPanel";

const TOKEN_KEY = "dcc_admin_token";

const TABS = [
  { value: "inquiry", label: "문의 관리" },
  { value: "pricing", label: "단가 관리" },
  { value: "portfolio", label: "포트폴리오 관리" },
  { value: "visit", label: "방문 분석" },
] as const;

type Tab = (typeof TABS)[number]["value"];

function LoginForm({ onLoggedIn }: { onLoggedIn: (token: string) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { token } = await api.post<{ token: string }>("/admin/login", { email, password });
      onLoggedIn(token);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "로그인에 실패했어요.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="mx-auto flex min-h-screen max-w-sm flex-col justify-center gap-6 px-6">
      <h1 className="wds-title-1 text-label-strong">어드민 로그인</h1>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="admin-email" className="wds-label-1 text-label-strong">
            이메일
          </label>
          <input
            id="admin-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="wds-body-2 rounded-md border border-line-neutral bg-background-normal px-4 py-2.5 text-label-normal"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="admin-password" className="wds-label-1 text-label-strong">
            비밀번호
          </label>
          <input
            id="admin-password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="wds-body-2 rounded-md border border-line-neutral bg-background-normal px-4 py-2.5 text-label-normal"
          />
        </div>
        {error && <output className="wds-body-2 text-status-negative">{error}</output>}
        <button
          type="submit"
          disabled={loading}
          className="inline-flex h-11 items-center justify-center rounded-md bg-primary px-6 wds-body-2 font-semibold text-static-white transition-colors duration-200 ease-standard hover:bg-primary-strong disabled:cursor-not-allowed disabled:opacity-50"
        >
          {loading ? "로그인 중…" : "로그인"}
        </button>
      </form>
    </section>
  );
}

function AdminDashboard({ token, onLogout }: { token: string; onLogout: () => void }) {
  const [tab, setTab] = useState<Tab>("inquiry");

  return (
    <section className="mx-auto flex max-w-layout flex-col gap-6 px-6 py-12">
      <div className="flex items-center justify-between gap-4">
        <h1 className="wds-title-1 text-label-strong">어드민</h1>
        <button type="button" onClick={onLogout} className="wds-label-1 text-label-neutral hover:text-primary">
          로그아웃
        </button>
      </div>

      <div className="flex gap-2 border-line-alternative border-b">
        {TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() => setTab(t.value)}
            aria-pressed={tab === t.value}
            className={`wds-label-1 -mb-px border-b-2 px-4 py-2.5 transition-colors duration-200 ease-standard ${
              tab === t.value ? "border-primary text-primary" : "border-transparent text-label-neutral"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "inquiry" && <InquiryPanel token={token} onLogout={onLogout} />}
      {tab === "pricing" && <PricingPanel token={token} onLogout={onLogout} />}
      {tab === "portfolio" && <PortfolioPanel token={token} onLogout={onLogout} />}
      {tab === "visit" && <VisitPanel token={token} onLogout={onLogout} />}
    </section>
  );
}

export function AdminPage() {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));

  const handleLoggedIn = (nextToken: string) => {
    localStorage.setItem(TOKEN_KEY, nextToken);
    setToken(nextToken);
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  return (
    <>
      <div className="px-6 pt-6">
        <Link to="/" className="wds-label-1 inline-flex items-center gap-1.5 text-label-neutral hover:text-primary">
          <Icon name="arrowLeft" size={16} />
          홈으로
        </Link>
      </div>
      {token ? <AdminDashboard token={token} onLogout={handleLogout} /> : <LoginForm onLoggedIn={handleLoggedIn} />}
    </>
  );
}

export default AdminPage;
